import { ResponsiveTable } from '../components/common/ResponsiveTable';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, getAuthHeaders } from '../config/api';
import { useThemedStyles } from '../theme/useThemedStyles';
import { ModalBackdrop } from '../components/common/ModalBackdrop';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { BolivianDateInput } from '../components/common/BolivianDateInput';

export interface ImportIssue {
  severity: 'error' | 'warning';
  sourceRow?: number | null;
  code: string;
  message: string;
}

export interface EditableImportRow {
  sourceRow: number;
  fingerprint: string;
  hc: string;
  patientName: string;
  treatmentName: string;
  date: string;
  grossAmount: number;
  total: number;
  paid: number;
  discountPercent: number;
  invoice: boolean;
  paymentMethod?: string | null;
  treatedTeeth?: string | null;
  specialMaterialsCost: number;
  isSpecialty: boolean;
  laboratoryCost: number;
  laboratoryName?: string | null;
  doctorName?: string | null;
  doctorCommissionOverride: number;
  importType: 'treatment' | 'payment';
}

interface PaymentAllocation {
  treatmentId: number;
  treatmentName: string;
  amount: number;
}

interface PreviewRow {
  values: EditableImportRow;
  debt: number;
  createsPatient: boolean;
  matchedPatientId?: number | null;
  matchedPatientName?: string | null;
  isImported: boolean;
  importRowId?: number | null;
  treatmentId?: number | null;
  paymentId?: number | null;
  issues: ImportIssue[];
  allocations: PaymentAllocation[];
}

interface Preview {
  tableName: string;
  range: string;
  snapshotHash: string;
  treatmentCount: number;
  paymentCount: number;
  existingPatientCount: number;
  newPatientCount: number;
  importedRowCount: number;
  remainingRowCount: number;
  issues: ImportIssue[];
  rows: PreviewRow[];
}

interface HistoryRow {
  importRowId: number;
  batchId: number;
  sourceRow: number;
  fingerprint: string;
  patientId: number;
  patientName: string;
  treatmentId?: number | null;
  paymentId?: number | null;
  createdPatient: boolean;
  values: EditableImportRow;
  debt: number;
  updatedBy?: string | null;
  updatedAt?: string | null;
  importType: 'treatment' | 'payment';
  allocations: PaymentAllocation[];
}

interface HistoryBatch {
  batchId: number;
  snapshotHash: string;
  importedBy: string;
  importedAt: string;
  rows: HistoryRow[];
}

interface History {
  tableName: string;
  batches: HistoryBatch[];
}

interface Doctor {
  id: number;
  firstName: string;
  lastName: string;
}

interface Props {
  token: string;
  onOpenPatient: (id: number) => void;
  onOpenTreatment: (treatmentId: number, patientId?: number | null) => void;
}

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const pageSize = 25;

export const currentSpanishMonth = (date = new Date()) => monthNames[date.getMonth()];
export const calculateImportDebt = (row: Pick<EditableImportRow, 'total' | 'paid'>) =>
  Math.max(0, Number(row.total || 0) - Number(row.paid || 0));
export const importDraftKey = (tableName: string, snapshotHash: string) =>
  `dentec:sheet-import-draft:${tableName.toLowerCase()}:${snapshotHash}`;

export const mergeStoredImportDraft = (
  previewRows: PreviewRow[],
  storedRows: EditableImportRow[] | null
) => {
  if (!storedRows) return previewRows.map(row => row.values);
  const stored = new Map(storedRows.map(row => [`${row.sourceRow}:${row.fingerprint}`, row]));
  return previewRows.map(row => {
    const saved = stored.get(`${row.values.sourceRow}:${row.values.fingerprint}`);
    return saved
      ? { ...row.values, ...saved, importType: saved.importType || row.values.importType }
      : row.values;
  });
};

type ThemeStyles = ReturnType<typeof useThemedStyles>;

const createPanelStyle = (theme: ThemeStyles): React.CSSProperties => ({
  background: theme.inputBg,
  border: `1px solid ${theme.border}`,
  borderRadius: 10,
  color: theme.text
});

const createInputStyle = (theme: ThemeStyles): React.CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  ...theme.input,
  borderRadius: 6,
  padding: '8px 10px'
});

const createButtonStyle = (theme: ThemeStyles, color = theme.primary): React.CSSProperties => ({
  ...theme.button,
  borderRadius: 6,
  padding: '9px 14px',
  background: color,
  borderColor: color
});

const parseResponse = async (response: Response) => {
  const data = await response.json().catch(() => ({}));
  if (response.ok) return data;
  const fallback: Record<number, string> = {
    400: 'Invalid request.',
    403: 'Administrator access is required.',
    404: 'The requested month or record was not found.',
    409: 'The sheet or selected rows changed. Refresh the preview.',
    422: 'Some selected values are invalid.'
  };
  const error = new Error(data.error || fallback[response.status] || `Request failed (${response.status}).`);
  (error as any).status = response.status;
  (error as any).data = data;
  throw error;
};

export default function GoogleSheetImportsPage({ token, onOpenPatient, onOpenTreatment }: Props) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const panel = createPanelStyle(theme);
  const input = createInputStyle(theme);
  const button = (color?: string) => createButtonStyle(theme, color);
  const [month, setMonth] = useState(currentSpanishMonth());
  const [preview, setPreview] = useState<Preview | null>(null);
  const [history, setHistory] = useState<History | null>(null);
  const [draftRows, setDraftRows] = useState<EditableImportRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [drawer, setDrawer] = useState<{ kind: 'draft'; sourceRow: number } | { kind: 'history'; row: HistoryRow } | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [laboratories, setLaboratories] = useState<{ id: number; name: string }[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'remaining' | 'errors' | 'warnings' | 'imported'>('remaining');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<'validate' | 'commit' | 'update' | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [validationIssues, setValidationIssues] = useState<ImportIssue[]>([]);

  const fetchJson = (path: string, init?: RequestInit) =>
    fetch(getApiUrl(path), {
      ...init,
      headers: { ...getAuthHeaders(token), ...(init?.headers || {}) }
    }).then(parseResponse);

  const loadMonth = async (requestedMonth = month) => {
    setLoading(true);
    setError('');
    setMessage('');
    setValidationIssues([]);
    try {
      const [nextPreview, nextHistory, nextDoctors, nextLaboratories] = await Promise.all([
        fetchJson('/sheets/imports/payments/preview', {
          method: 'POST',
          body: JSON.stringify({ tableName: requestedMonth })
        }),
        fetchJson(`/sheets/imports/payments/${encodeURIComponent(requestedMonth)}/history`),
        fetchJson('/doctors'),
        fetchJson('/laboratories')
      ]);
      const key = importDraftKey(requestedMonth, nextPreview.snapshotHash);
      let storedRows: EditableImportRow[] | null = null;
      try {
        storedRows = JSON.parse(localStorage.getItem(key) || 'null');
      } catch {
        localStorage.removeItem(key);
      }
      const merged = mergeStoredImportDraft(nextPreview.rows, storedRows);
      setPreview(nextPreview);
      setHistory(nextHistory);
      setDoctors(nextDoctors);
      setLaboratories(nextLaboratories);
      setDraftRows(merged);
      setSelected(new Set(
        nextPreview.rows
          .filter((row: PreviewRow) =>
            !row.isImported && row.issues.every(issue => issue.severity !== 'error'))
          .map((row: PreviewRow) => row.values.sourceRow)
      ));
      setPage(1);

      const draftPrefix = `dentec:sheet-import-draft:${requestedMonth.toLowerCase()}:`;
      Object.keys(localStorage)
        .filter(keyName => keyName.startsWith(draftPrefix) && keyName !== key)
        .forEach(keyName => {
          localStorage.removeItem(keyName);
          setMessage(t('sheetImports.snapshotDraftDiscarded'));
        });
    } catch (err: any) {
      setError(err.message);
      setPreview(null);
      setHistory(null);
      setDraftRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Loading on month/token change is intentional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMonth(month); }, [month, token]);

  useEffect(() => {
    if (!preview || draftRows.length === 0) return;
    localStorage.setItem(importDraftKey(month, preview.snapshotHash), JSON.stringify(draftRows));
  }, [draftRows, month, preview]);

  const previewBySource = useMemo(
    () => new Map((preview?.rows || []).map(row => [row.values.sourceRow, row])),
    [preview]
  );
  const draftBySource = useMemo(
    () => new Map(draftRows.map(row => [row.sourceRow, row])),
    [draftRows]
  );
  const displayedRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (preview?.rows || []).filter(row => {
      const values = draftBySource.get(row.values.sourceRow) || row.values;
      const matchesSearch = !query ||
        `${values.hc} ${values.patientName} ${values.treatmentName} ${values.doctorName || ''}`
          .toLowerCase()
          .includes(query);
      if (!matchesSearch) return false;
      if (filter === 'remaining') return !row.isImported;
      if (filter === 'imported') return row.isImported;
      if (filter === 'errors') return row.issues.some(issue => issue.severity === 'error');
      if (filter === 'warnings') return row.issues.some(issue => issue.severity === 'warning');
      return true;
    });
  }, [preview, draftBySource, search, filter]);
  const pageCount = Math.max(1, Math.ceil(displayedRows.length / pageSize));
  const pagedRows = displayedRows.slice((page - 1) * pageSize, page * pageSize);
  const selectedRows = draftRows.filter(row => selected.has(row.sourceRow));

  const updateDraft = (sourceRow: number, patch: Partial<EditableImportRow>) => {
    setDraftRows(rows => rows.map(row => row.sourceRow === sourceRow ? { ...row, ...patch } : row));
  };

  const toggleSelected = (sourceRow: number) => {
    setSelected(current => {
      const next = new Set(current);
      next.has(sourceRow) ? next.delete(sourceRow) : next.add(sourceRow);
      return next;
    });
  };

  const editPreviewRow = (row: PreviewRow) => {
    if (!row.isImported) {
      setDrawer({ kind: 'draft', sourceRow: row.values.sourceRow });
      return;
    }

    const historyRow = (history?.batches.flatMap(batch => batch.rows) || [])
      .find(item => item.importRowId === row.importRowId);
    if (!historyRow) {
      setError(t('sheetImports.historyRowUnavailable'));
      return;
    }

    if (historyRow.importType === 'treatment') {
      setDrawer({ kind: 'history', row: historyRow });
    }
  };

  const validateSelected = async () => {
    if (!preview) return;
    setBusy('validate');
    setError('');
    setMessage('');
    try {
      const result = await fetchJson('/sheets/imports/payments/validate', {
        method: 'POST',
        body: JSON.stringify({
          tableName: month,
          snapshotHash: preview.snapshotHash,
          rows: selectedRows
        })
      });
      setValidationIssues(result.issues || []);
      setPreview(current => current ? {
        ...current,
        rows: current.rows.map(row => {
          const validated = (result.rows || []).find((item: PreviewRow) =>
            item.values.sourceRow === row.values.sourceRow);
          return validated ? { ...row, allocations: validated.allocations } : row;
        })
      } : current);
      setMessage(t('sheetImports.validationPassed', { count: selectedRows.length }));
    } catch (err: any) {
      setValidationIssues(err.data?.issues || []);
      setError(err.message);
      if (err.status === 409) await loadMonth();
    } finally {
      setBusy(null);
    }
  };

  const commitSelected = async () => {
    if (!preview || selectedRows.length === 0) return;
    if (!window.confirm(t('sheetImports.confirmImport', { count: selectedRows.length, month }))) return;
    setBusy('commit');
    setError('');
    setMessage('');
    try {
      const result = await fetchJson('/sheets/imports/payments/commit', {
        method: 'POST',
        body: JSON.stringify({
          tableName: month,
          snapshotHash: preview.snapshotHash,
          rows: selectedRows
        })
      });
      localStorage.removeItem(importDraftKey(month, preview.snapshotHash));
      setMessage(t('sheetImports.importComplete', { treatments: 0, payments: result.paymentsCreated }));
      await loadMonth();
    } catch (err: any) {
      setValidationIssues(err.data?.issues || []);
      setError(err.message);
      if (err.status === 409) await loadMonth();
    } finally {
      setBusy(null);
    }
  };

  const cards = preview ? [
    [t('sheetImports.payments'), preview.paymentCount],
    [t('sheetImports.existingPatients'), preview.existingPatientCount],
    [t('sheetImports.imported'), preview.importedRowCount],
    [t('sheetImports.remaining'), preview.remainingRowCount],
    [t('sheetImports.errors'), preview.issues.filter(issue => issue.severity === 'error').length],
    [t('sheetImports.warnings'), preview.issues.filter(issue => issue.severity === 'warning').length]
  ] : [];

  const drawerValues = drawer?.kind === 'draft'
    ? draftBySource.get(drawer.sourceRow)
    : drawer?.kind === 'history'
      ? drawer.row.values
      : null;

  return (
    <div style={{ color: theme.text, display: 'grid', gap: 14 }}>
      <header className="responsive-row" style={{ display: 'flex', gap: 12, alignItems: 'end', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0 }}>{t('sheetImports.title')}</h2>
          <div style={{ color: theme.muted, marginTop: 4 }}>{t('sheetImports.subtitle')}</div>
        </div>
        <div className="responsive-row" style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12, color: theme.muted }}>{t('sheetImports.month')}</span>
            <select value={month} onChange={event => setMonth(event.target.value)} style={{ ...input, minWidth: 160 }}>
              {monthNames.map(name => <option key={name}>{name}</option>)}
            </select>
          </label>
          <button style={button('#475569')} onClick={() => loadMonth()} disabled={loading}>
            {t('common.refresh')}
          </button>
        </div>
      </header>

      {!!message && <div style={{ ...panel, padding: 10, borderColor: '#166534', color: '#86efac' }}>{message}</div>}
      {!!error && <div style={{ ...panel, padding: 10, borderColor: '#991b1b', color: '#fca5a5' }}>{error}</div>}

      <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
        {cards.map(([label, value]) => (
          <div key={String(label)} style={{ ...panel, padding: 12 }}>
            <div style={{ color: theme.muted, fontSize: 12 }}>{label}</div>
            <strong style={{ fontSize: 22 }}>{value}</strong>
          </div>
        ))}
      </div>

      <section style={{ ...panel, overflow: 'hidden' }}>
        <div className="responsive-row" style={{ padding: 12, display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: `1px solid ${theme.border}` }}>
          <input
            value={search}
            onChange={event => { setSearch(event.target.value); setPage(1); }}
            placeholder={t('sheetImports.search')}
            style={{ ...input, maxWidth: 340 }}
          />
          <select value={filter} onChange={event => { setFilter(event.target.value as any); setPage(1); }} style={{ ...input, width: 160 }}>
            {(['all', 'remaining', 'errors', 'warnings', 'imported'] as const).map(value =>
              <option key={value} value={value}>{t(`sheetImports.filter.${value}`)}</option>
            )}
          </select>
          <div style={{ flex: 1 }} />
          <button style={button('#0f766e')} onClick={validateSelected} disabled={busy !== null || selectedRows.length === 0}>
            {busy === 'validate' ? t('sheetImports.validating') : t('sheetImports.validateSelected', { count: selectedRows.length })}
          </button>
          <button style={button()} onClick={commitSelected} disabled={busy !== null || selectedRows.length === 0}>
            {busy === 'commit' ? t('sheetImports.importing') : t('sheetImports.importSelected', { count: selectedRows.length })}
          </button>
        </div>

        {loading ? <div style={{ padding: 20 }}>{t('common.loading')}</div> : (
          <div className="responsive-table-scroll" style={{ overflowX: 'auto' }}>
            <ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1050 }}>
              <thead>
                <tr style={{ background: theme.surface, textAlign: 'left' }}>
                  <th style={{ padding: 8 }} />
                  <th style={{ padding: 8 }}>{t('sheetImports.row')}</th>
                  <th style={{ padding: 8 }}>{t('sheetImports.importType')}</th>
                  <th style={{ padding: 8 }}>HC</th>
                  <th style={{ padding: 8 }}>{t('common.patient')}</th>
                  <th style={{ padding: 8 }}>{t('common.treatment')}</th>
                  <th style={{ padding: 8 }}>{t('common.date')}</th>
                  <th style={{ padding: 8 }}>{t('sheetImports.total')}</th>
                  <th style={{ padding: 8 }}>{t('sheetImports.paid')}</th>
                  <th style={{ padding: 8 }}>{t('sheetImports.debt')}</th>
                  <th style={{ padding: 8 }}>{t('sheetImports.doctor')}</th>
                  <th style={{ padding: 8 }}>{t('sheetImports.status')}</th>
                  <th style={{ padding: 8 }}>{t('sheetImports.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(row => {
                  const values = draftBySource.get(row.values.sourceRow) || row.values;
                  const issues = [
                    ...row.issues,
                    ...validationIssues.filter(issue => issue.sourceRow === values.sourceRow)
                  ];
                  return (
                    <tr key={values.sourceRow} style={{ borderTop: `1px solid ${theme.border}`, opacity: row.isImported ? 0.65 : 1 }}>
                      <td style={{ padding: 8 }}>
                        <input
                          type="checkbox"
                          checked={selected.has(values.sourceRow)}
                          disabled={row.isImported}
                          onChange={() => toggleSelected(values.sourceRow)}
                        />
                      </td>
                      <td style={{ padding: 8 }}>{values.sourceRow}</td>
                      <td style={{ padding: 8 }}>
                        {t(`sheetImports.importTypes.${values.importType}`)}
                      </td>
                      <td style={{ padding: 8 }}>{values.hc}</td>
                      <td style={{ padding: 8 }}>{values.patientName}</td>
                      <td style={{ padding: 8 }}>
                        <div>{values.treatmentName}</div>
                        {values.importType === 'payment' && row.allocations?.length > 0 && (
                          <div style={{ color: theme.muted, fontSize: 11, marginTop: 3 }}>
                            {row.allocations.map(allocation =>
                              `#${allocation.treatmentId} ${allocation.treatmentName}: ${Number(allocation.amount).toFixed(2)}`
                            ).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: 8 }}>{formatDate(values.date)}</td>
                      <td style={{ padding: 8 }}>{values.importType === 'payment' ? '-' : Number(values.total).toFixed(2)}</td>
                      <td style={{ padding: 8 }}>{Number(values.paid).toFixed(2)}</td>
                      <td style={{ padding: 8 }}>{values.importType === 'payment' ? '-' : calculateImportDebt(values).toFixed(2)}</td>
                      <td style={{ padding: 8 }}>{values.importType === 'payment' ? '-' : values.doctorName || '-'}</td>
                      <td style={{ padding: 8 }}>
                        {row.isImported ? (
                          <span style={{ color: '#86efac' }}>{t('sheetImports.imported')}</span>
                        ) : issues.some(issue => issue.severity === 'error') ? (
                          <span title={issues.map(issue => issue.message).join('\n')} style={{ color: '#fca5a5' }}>
                            {t('sheetImports.errors')} ({issues.filter(issue => issue.severity === 'error').length})
                          </span>
                        ) : issues.length > 0 ? (
                          <span title={issues.map(issue => issue.message).join('\n')} style={{ color: '#fde68a' }}>
                            {t('sheetImports.warnings')} ({issues.length})
                          </span>
                        ) : <span style={{ color: '#86efac' }}>{t('sheetImports.ready')}</span>}
                      </td>
                      <td style={{ padding: 8 }}>
                        <button
                          style={button(row.isImported ? '#475569' : '#334155')}
                          onClick={() => editPreviewRow(row)}
                          disabled={row.isImported && values.importType === 'payment'}
                        >
                          {row.isImported && values.importType === 'payment'
                            ? t('sheetImports.readOnly')
                            : t('sheetImports.edit')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></ResponsiveTable>
          </div>
        )}
        <div className="responsive-row" style={{ padding: 10, display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${theme.border}` }}>
          <span>{t('sheetImports.showing', { count: displayedRows.length })}</span>
          <div className="responsive-row" style={{ display: 'flex', gap: 6 }}>
            <button style={button('#334155')} disabled={page <= 1} onClick={() => setPage(value => value - 1)}>{'<'}</button>
            <span style={{ padding: 8 }}>{page} / {pageCount}</span>
            <button style={button('#334155')} disabled={page >= pageCount} onClick={() => setPage(value => value + 1)}>{'>'}</button>
          </div>
        </div>
      </section>

      <section style={{ ...panel, padding: 12 }}>
        <h3 style={{ marginTop: 0 }}>{t('sheetImports.history')}</h3>
        {(history?.batches || []).length === 0 ? <div style={{ color: theme.muted }}>{t('sheetImports.noHistory')}</div> : (
          history!.batches.map(batch => (
            <details key={batch.batchId} style={{ borderTop: `1px solid ${theme.border}`, padding: '10px 0' }}>
              <summary style={{ cursor: 'pointer' }}>
                {formatDateTime(batch.importedAt)} · {batch.importedBy} · {batch.rows.length} {t('sheetImports.rows')}
              </summary>
              <div className="responsive-table-scroll" style={{ overflowX: 'auto', marginTop: 8 }}>
                <ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 750 }}>
                  <tbody>
                    {batch.rows.map(row => (
                      <tr key={row.importRowId} style={{ borderTop: `1px solid ${theme.border}` }}>
                        <td style={{ padding: 8 }}>#{row.sourceRow}</td>
                        <td style={{ padding: 8 }}>
                          <button style={{ ...button('#334155'), padding: '5px 8px' }} onClick={() => onOpenPatient(row.patientId)}>
                            {row.patientName}
                          </button>
                        </td>
                        <td style={{ padding: 8 }}>
                          <div>{t(`sheetImports.importTypes.${row.importType}`)} · {row.values.treatmentName}</div>
                          {row.allocations?.length > 0 && (
                            <div style={{ color: theme.muted, fontSize: 11, marginTop: 3 }}>
                              {row.allocations.map(allocation =>
                                `#${allocation.treatmentId} ${allocation.treatmentName}: ${Number(allocation.amount).toFixed(2)}`
                              ).join(' · ')}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: 8 }}>
                          {row.treatmentId ? (
                            <button style={{ ...button('#334155'), padding: '5px 8px' }} onClick={() => onOpenTreatment(row.treatmentId!, row.patientId)}>
                              #{row.treatmentId}
                            </button>
                          ) : '-'}
                        </td>
                        <td style={{ padding: 8 }}>
                          {row.allocations?.length
                            ? `${row.allocations.length} ${t('sheetImports.payments').toLowerCase()}`
                            : row.paymentId
                              ? `${t('sheetImports.payment')} #${row.paymentId}`
                              : '-'}
                        </td>
                        <td style={{ padding: 8 }}>
                          {row.importType === 'treatment' ? (
                            <button style={{ ...button('#475569'), padding: '5px 8px' }} onClick={() => setDrawer({ kind: 'history', row })}>
                              {t('sheetImports.edit')}
                            </button>
                          ) : t('sheetImports.readOnly')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></ResponsiveTable>
              </div>
            </details>
          ))
        )}
      </section>

      {drawer && drawerValues && (
        <ImportRowDrawer
          key={`${drawer.kind}:${drawerValues.sourceRow}:${drawer.kind === 'history' ? drawer.row.updatedAt || '' : ''}`}
          values={drawerValues}
          issues={drawer.kind === 'draft' ? previewBySource.get(drawer.sourceRow)?.issues || [] : []}
          doctors={doctors}
          laboratories={laboratories}
          patientLocked={drawer.kind === 'history'}
          saving={busy === 'update'}
          onClose={() => setDrawer(null)}
          onSave={drawer.kind === 'draft' ? values => {
            updateDraft(drawer.sourceRow, values);
            setDrawer(null);
          } : () => {}}
        />
      )}
    </div>
  );
}

function ImportRowDrawer({
  values,
  issues,
  doctors,
  laboratories,
  patientLocked,
  saving,
  onClose,
  onSave
}: {
  values: EditableImportRow;
  issues: ImportIssue[];
  doctors: Doctor[];
  laboratories: { id: number; name: string }[];
  patientLocked: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (values: EditableImportRow) => void;
}) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const input = createInputStyle(theme);
  const button = (color?: string) => createButtonStyle(theme, color);
  const [form, setForm] = useState(values);
  const isPayment = form.importType === 'payment';
  const set = (patch: Partial<EditableImportRow>) => setForm(current => ({ ...current, ...patch }));
  const number = (key: keyof EditableImportRow) => (event: React.ChangeEvent<HTMLInputElement>) =>
    set({ [key]: Number(event.target.value) } as Partial<EditableImportRow>);
  const field = (label: string, control: React.ReactNode) => (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ color: theme.muted, fontSize: 12 }}>{label}</span>
      {control}
    </label>
  );

  return (
    <ModalBackdrop style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 2000 }} onDismiss={onClose}>
      <aside
        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 'min(560px, 96vw)', background: theme.surface, padding: 18, overflowY: 'auto', color: theme.text, boxShadow: '-8px 0 24px #0008', borderLeft: `1px solid ${theme.border}` }}
        onMouseDown={event => event.stopPropagation()}
      >
        <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>{t('sheetImports.editRow', { row: form.sourceRow })}</h3>
          <button style={button('#475569')} onClick={onClose}>X</button>
        </div>
        {issues.map((issue, index) => (
          <div key={`${issue.code}:${index}`} style={{ color: issue.severity === 'error' ? '#fca5a5' : '#fde68a', marginBottom: 5 }}>
            {issue.message}
          </div>
        ))}
        <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>{field(t('sheetImports.importType'), <input style={input} value={t('sheetImports.importTypes.payment')} readOnly />)}</div>
          {field('HC', <input style={input} value={form.hc} disabled={patientLocked} onChange={event => set({ hc: event.target.value })} />)}
          {field(t('common.patient'), <input style={input} value={form.patientName} disabled={patientLocked} onChange={event => set({ patientName: event.target.value })} />)}
          <div style={{ gridColumn: '1 / -1' }}>{field(
            isPayment ? t('sheetImports.description') : t('common.treatment'),
            <input style={input} value={form.treatmentName} onChange={event => set({ treatmentName: event.target.value })} />
          )}</div>
          {field(t('common.date'), <BolivianDateInput style={input} value={form.date.slice(0, 10)} onChange={date => set({ date: date ? `${date}T00:00:00Z` : '' })} />)}
          {field(t('sheetImports.teeth'), <input style={input} disabled={isPayment} value={form.treatedTeeth || ''} onChange={event => set({ treatedTeeth: event.target.value })} />)}
          {field(t('sheetImports.gross'), <input type="number" step="0.01" style={input} disabled={isPayment} value={form.grossAmount} onChange={number('grossAmount')} />)}
          {field(t('sheetImports.total'), <input type="number" step="0.01" style={input} disabled={isPayment} value={form.total} onChange={number('total')} />)}
          {field(t('sheetImports.paid'), <input type="number" step="0.01" style={input} value={form.paid} onChange={number('paid')} />)}
          {field(t('sheetImports.debt'), <input style={input} readOnly value={isPayment ? '-' : calculateImportDebt(form).toFixed(2)} />)}
          {field(t('sheetImports.discount'), <input type="number" step="0.01" style={input} disabled={isPayment} value={form.discountPercent} onChange={number('discountPercent')} />)}
          {field(t('sheetImports.paymentMethod'), <input style={input} value={form.paymentMethod || ''} onChange={event => set({ paymentMethod: event.target.value })} />)}
          {field(t('sheetImports.doctor'), (
            <select style={input} disabled={isPayment} value={form.doctorName || ''} onChange={event => set({ doctorName: event.target.value })}>
              <option value="">-</option>
              {doctors.map(doctor => {
                const name = `${doctor.firstName} ${doctor.lastName}`.trim();
                return <option key={doctor.id} value={doctor.firstName}>{name}</option>;
              })}
            </select>
          ))}
          {field(t('sheetImports.commission'), <input type="number" step="0.01" style={input} disabled={isPayment} value={form.doctorCommissionOverride} onChange={number('doctorCommissionOverride')} />)}
          {field(t('sheetImports.laboratory'), (
            <>
              <input style={input} disabled={isPayment} list="sheet-import-labs" value={form.laboratoryName || ''} onChange={event => set({ laboratoryName: event.target.value })} />
              <datalist id="sheet-import-labs">{laboratories.map(lab => <option key={lab.id} value={lab.name} />)}</datalist>
            </>
          ))}
          {field(t('sheetImports.laboratoryCost'), <input type="number" step="0.01" style={input} disabled={isPayment} value={form.laboratoryCost} onChange={number('laboratoryCost')} />)}
          {field(t('sheetImports.materialCost'), <input type="number" step="0.01" style={input} disabled={isPayment} value={form.specialMaterialsCost} onChange={number('specialMaterialsCost')} />)}
          {field(t('sheetImports.invoice'), <input type="checkbox" disabled={isPayment} checked={form.invoice} onChange={event => set({ invoice: event.target.checked })} />)}
          {field(t('sheetImports.specialty'), <input type="checkbox" disabled={isPayment} checked={form.isSpecialty} onChange={event => set({ isSpecialty: event.target.checked })} />)}
        </div>
        <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button style={button('#475569')} onClick={onClose}>{t('sheetImports.cancel')}</button>
          <button style={button()} disabled={saving} onClick={() => onSave(form)}>
            {saving ? t('sheetImports.saving') : t('sheetImports.save')}
          </button>
        </div>
      </aside>
    </ModalBackdrop>
  );
}
